<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKanbanStore } from 'dashboard/stores/kanban';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KanbanDealContacts from './KanbanDealContacts.vue';
import KanbanDealConversations from './KanbanDealConversations.vue';
import KanbanDealTimeline from './KanbanDealTimeline.vue';
import KanbanMergeDialog from './KanbanMergeDialog.vue';
import { recusaDeOutroAberto, motivoDaRecusa } from './helpers/recusaDoMover';

const props = defineProps({
  cardId: { type: [Number, String], default: null },
  currency: { type: String, default: 'BRL' },
  showValue: { type: Boolean, default: false },
  canManage: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'manageFields', 'openCard']);

const { t } = useI18n();
const kanban = useKanbanStore();

const details = computed(() => kanban.details);
// CRM-04 R2-T7: o negócio tem N contatos; o principal vem primeiro.
const principal = computed(() => details.value?.contacts?.[0] || {});

// --- card name -------------------------------------------------------------
// The card carries its own name only when someone set one; empty means "named
// after the person", so clearing the field is how you go back to that. Sem título, o nome é o do
// contato principal, e quem decide é o servidor (`display_name`).
const isEditingName = ref(false);
const nameDraft = ref('');
const nameInput = ref(null);

const cardName = computed(
  () =>
    details.value?.display_name ||
    details.value?.title ||
    t('KANBAN.CARD.NO_NAME')
);

const startEditingName = async () => {
  nameDraft.value = details.value?.title || '';
  isEditingName.value = true;
  await nextTick();
  nameInput.value?.focus();
  nameInput.value?.select();
};

const cancelEditingName = () => {
  isEditingName.value = false;
};

const saveName = async () => {
  if (!isEditingName.value) return;
  const next = nameDraft.value.trim();
  isEditingName.value = false;
  if (next === (details.value?.title || '')) return;
  await kanban.updateCardTitle(details.value.id, next);
};

// --- remover do board ---------------------------------------------------
// Confirmed because it is not obvious from the button that the conversation
// survives — people read "delete" as "delete everything".
const deleteDialogRef = ref(null);
const isDeleting = ref(false);

// CRM-04 (N13): apaga quem vê todas as conversas do negócio, ou o administrador. O servidor recusa com 403 e o
// motivo em `error`.
const recusaAoRemover = error => {
  const codigo =
    error?.response?.status === 403 ? error.response.data?.error : null;
  if (codigo === 'not_all_conversations_visible')
    return t('KANBAN.DRAWER.DELETE_NOT_ALL_VISIBLE');
  return t('KANBAN.DRAWER.DELETE_ERROR');
};

const confirmDelete = async () => {
  if (isDeleting.value) return;
  isDeleting.value = true;
  try {
    await kanban.deleteCard(details.value.id);
    emit('close');
  } catch (error) {
    useAlert(recusaAoRemover(error));
  } finally {
    isDeleting.value = false;
  }
};

const formattedValue = computed(() => {
  if (!details.value?.value_cents) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.currency || 'BRL',
  }).format(details.value.value_cents / 100);
});

const summaryDisabledReason = computed(() => {
  if (!details.value) return null;
  if (!details.value.summary_available)
    return t('KANBAN.DRAWER.SUMMARY_UNAVAILABLE');
  // CRM-04: até a R3 o resumo é o da conversa de origem, que o servidor manda nula para quem não a vê.
  if (!details.value.conversation) return t('KANBAN.DRAWER.SUMMARY_HIDDEN');
  if (!details.value.summarizable) return t('KANBAN.DRAWER.SUMMARY_TOO_SHORT');
  return null;
});

const onGenerateSummary = async () => {
  try {
    await kanban.generateSummary(props.cardId);
  } catch (error) {
    const code = error?.response?.data?.error;
    const messages = {
      not_enough_content: t('KANBAN.DRAWER.SUMMARY_TOO_SHORT'),
      not_configured: t('KANBAN.DRAWER.SUMMARY_UNAVAILABLE'),
      conversation_hidden: t('KANBAN.DRAWER.SUMMARY_HIDDEN'),
    };
    useAlert(messages[code] || t('KANBAN.DRAWER.SUMMARY_FAILED'));
  }
};

// --- juntar ------------------------------------------------------------------
// CRM-04 R2-T7 (N15): só o administrador vê o botão; a recusa é do servidor. Depois de juntar, o
// painel relê o negócio: os contatos, as conversas e o histórico do absorvido vieram para ele.
const mergeDialogRef = ref(null);

const abrirJuntar = preescolhido => mergeDialogRef.value?.open(preescolhido);

const aoJuntar = async cardId => {
  try {
    await kanban.refreshDeal(cardId);
  } catch {
    useAlert(t('KANBAN.DRAWER.LOAD_FAILED'));
  }
};

// --- a recusa do mover ---------------------------------------------------------
// CRM-04 (revisão de 02/10, RN-02): um contato deste negócio já tem outro aberto no funil, e o servidor recusou
// levá-lo a uma etapa aberta (pelo seletor daqui ou pelo arrastar no quadro). O motivo, o caminho para o outro
// negócio (só quando a pessoa o vê) e, para o administrador, juntar o outro a este; depois disso, mover de novo.
const recusaDoMover = computed(() =>
  details.value && kanban.moveRefusal?.cardId === details.value.id
    ? kanban.moveRefusal
    : null
);

const juntarComOAberto = () =>
  abrirJuntar({
    id: recusaDoMover.value.otherCardId,
    display_name: t('KANBAN.MERGE.OPEN_DEAL_OF', {
      name: recusaDoMover.value.contactName || t('KANBAN.CARD.NO_NAME'),
    }),
  });

// --- etapa -------------------------------------------------------------------
// CRM-04 C7 (K7): a etapa muda aqui pela mesma ação do arrastar, com a mesma permissão (quem
// vê a conversa move o card). As opções são as do funil do card: trocar de funil no quadro não
// fecha o painel.
const stageOptions = computed(
  () =>
    kanban.pipelines.find(p => p.id === details.value?.pipeline_id)?.stages ||
    kanban.stages
);
const stageDraft = ref(null);
const isMovingStage = ref(false);

watch(
  () => details.value?.stage_id,
  stageId => {
    stageDraft.value = stageId;
  },
  { immediate: true }
);

const onChangeStage = async () => {
  const { id, stage_id: fromStageId } = details.value;
  isMovingStage.value = true;
  try {
    // Topo da etapa nova, onde também cai o card criado e o movido por outra pessoa.
    await kanban.moveCard({
      cardId: id,
      fromStageId,
      toStageId: stageDraft.value,
      newIndex: 0,
    });
  } catch (error) {
    stageDraft.value = fromStageId;
    // RN-02: nada mudou, e o motivo aparece logo abaixo do seletor (`recusaDoMover`).
    if (recusaDeOutroAberto(error)) return;
    useAlert(t('KANBAN.MOVE_FAILED'));
    await kanban.fetchBoard();
    return;
  } finally {
    isMovingStage.value = false;
  }
  // Relido para a linha do tempo logo abaixo ganhar a passagem que acabou de acontecer.
  try {
    await kanban.refreshDeal(id);
  } catch {
    useAlert(t('KANBAN.DRAWER.LOAD_FAILED'));
  }
};
</script>

<template>
  <aside
    class="flex flex-col flex-shrink-0 w-[380px] h-full border-l bg-n-solid-1 border-n-weak overflow-y-auto pz-scrollbar"
  >
    <header
      class="flex items-center gap-2 px-4 py-3 border-b border-n-weak sticky top-0 bg-n-solid-1 z-10"
    >
      <input
        v-if="isEditingName"
        ref="nameInput"
        v-model="nameDraft"
        type="text"
        class="flex-1 min-w-0 px-2 py-1 text-sm font-medium border rounded-md bg-n-alpha-1 border-n-brand text-n-slate-12"
        :placeholder="principal.name || $t('KANBAN.CARD.NO_NAME')"
        :aria-label="$t('KANBAN.DRAWER.RENAME')"
        @keyup.enter="saveName"
        @keyup.esc="cancelEditingName"
        @blur="saveName"
      />
      <h2 v-else class="flex-1 text-sm font-medium truncate text-n-slate-12">
        {{ cardName }}
      </h2>
      <button
        v-if="!isEditingName"
        class="flex items-center justify-center rounded-md size-7 text-n-slate-11 hover:bg-n-alpha-2"
        :aria-label="$t('KANBAN.DRAWER.RENAME')"
        @click="startEditingName"
      >
        <Icon icon="i-lucide-pencil" class="size-3.5" />
      </button>
      <button
        v-if="!isEditingName && canManage && details"
        type="button"
        data-juntar
        class="flex items-center justify-center rounded-md size-7 text-n-slate-11 hover:bg-n-alpha-2"
        :aria-label="$t('KANBAN.MERGE.BUTTON')"
        :title="$t('KANBAN.MERGE.BUTTON')"
        @click="abrirJuntar()"
      >
        <Icon icon="i-lucide-merge" class="size-3.5" />
      </button>
      <button
        v-if="!isEditingName"
        class="flex items-center justify-center rounded-md size-7 text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
        :aria-label="$t('KANBAN.DRAWER.DELETE')"
        :title="$t('KANBAN.DRAWER.DELETE')"
        @click="deleteDialogRef?.open()"
      >
        <Icon icon="i-lucide-trash-2" class="size-3.5" />
      </button>
      <button
        class="flex items-center justify-center rounded-md size-7 text-n-slate-11 hover:bg-n-alpha-2"
        :aria-label="$t('KANBAN.DRAWER.CLOSE')"
        @click="emit('close')"
      >
        <Icon icon="i-lucide-x" class="size-4" />
      </button>
    </header>

    <div
      v-if="kanban.uiFlags.fetchingDetails && !details"
      class="flex items-center justify-center flex-1"
    >
      <Spinner class="text-n-brand" />
    </div>

    <div v-else-if="details" class="flex flex-col gap-5 p-4">
      <section class="flex items-center gap-3">
        <Avatar
          :name="principal.name || '?'"
          :src="principal.thumbnail || principal.avatar_url || ''"
          :size="40"
          rounded-full
        />
        <div class="flex flex-col min-w-0 gap-1">
          <div
            class="flex items-center min-w-0 gap-1.5 text-xs text-n-slate-11"
          >
            <select
              v-model="stageDraft"
              :disabled="isMovingStage"
              :aria-label="$t('KANBAN.DRAWER.STAGE')"
              class="h-7 px-2 text-xs rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand disabled:opacity-50"
              @change="onChangeStage"
            >
              <option
                v-for="stage in stageOptions"
                :key="stage.id"
                :value="stage.id"
              >
                {{ stage.name }}
              </option>
            </select>
          </div>
          <span
            v-if="showValue && formattedValue"
            class="text-sm font-medium text-n-teal-11"
          >
            {{ formattedValue }}
          </span>
        </div>
      </section>

      <div
        v-if="recusaDoMover"
        data-recusa-mover
        class="flex items-start gap-2 p-2 text-xs border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-slate-12"
      >
        <Icon icon="i-lucide-info" class="mt-0.5 size-3.5 flex-shrink-0" />
        <div class="flex flex-col gap-1">
          <span>{{ motivoDaRecusa(recusaDoMover, t) }}</span>
          <span class="text-n-slate-11">
            {{
              canManage
                ? $t('KANBAN.MOVE_REFUSED.MERGE_HINT')
                : $t('KANBAN.MOVE_REFUSED.ASK_ADMIN')
            }}
          </span>
          <div v-if="recusaDoMover.otherCardId" class="flex items-center gap-3">
            <button
              type="button"
              data-abrir-aberto
              class="font-medium text-n-brand hover:underline"
              @click="emit('openCard', recusaDoMover.otherCardId)"
            >
              {{ $t('KANBAN.DRAWER.OPEN_OTHER_DEAL') }}
            </button>
            <button
              v-if="canManage"
              type="button"
              data-juntar-aberto
              class="font-medium text-n-brand hover:underline"
              @click="juntarComOAberto"
            >
              {{ $t('KANBAN.MOVE_REFUSED.MERGE') }}
            </button>
          </div>
        </div>
      </div>

      <KanbanDealContacts
        :can-manage="canManage"
        @manage-fields="emit('manageFields')"
        @open-card="id => emit('openCard', id)"
        @merge="abrirJuntar"
      />

      <KanbanDealConversations
        :conversations="details.conversations || []"
        :contacts="details.contacts || []"
        :hidden-count="details.hidden_conversations_count || 0"
      />

      <section class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <h3 class="flex-1 text-xs font-medium uppercase text-n-slate-10">
            {{ $t('KANBAN.DRAWER.SUMMARY_SECTION') }}
          </h3>
          <button
            :disabled="
              !!summaryDisabledReason || kanban.uiFlags.generatingSummary
            "
            :title="summaryDisabledReason || undefined"
            class="flex items-center gap-1 px-2 h-7 text-xs rounded-md text-n-brand hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="onGenerateSummary"
          >
            <Icon
              icon="i-lucide-sparkles"
              class="size-3.5"
              :class="{ 'animate-pulse': kanban.uiFlags.generatingSummary }"
            />
            {{
              details.summary
                ? $t('KANBAN.DRAWER.REGENERATE')
                : $t('KANBAN.DRAWER.GENERATE')
            }}
          </button>
        </div>
        <p
          v-if="details.summary"
          class="p-3 text-sm whitespace-pre-line rounded-lg text-n-slate-12 bg-n-alpha-1"
        >
          {{ details.summary }}
        </p>
        <p v-else class="text-xs text-n-slate-10">
          {{ summaryDisabledReason || $t('KANBAN.DRAWER.NO_SUMMARY') }}
        </p>
      </section>

      <KanbanDealTimeline
        :card-id="details.id"
        :stages="stageOptions"
        :conversations="details.conversations || []"
      />
    </div>
    <KanbanMergeDialog
      v-if="canManage && details"
      ref="mergeDialogRef"
      :card="details"
      :stages="stageOptions"
      @merged="aoJuntar"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('KANBAN.DRAWER.DELETE_CONFIRM_TITLE')"
      :description="$t('KANBAN.DRAWER.DELETE_CONFIRM_DESCRIPTION')"
      :confirm-button-label="$t('KANBAN.DRAWER.DELETE')"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </aside>
</template>
